import { pick } from 'lodash';
import React, { useCallback } from 'react';
import { Grid } from 'antd';
import RGL, { WidthProvider } from 'react-grid-layout';
import { DashboardWidget as Widget, WidgetGridItem } from '../../components/DashboardWidget';
import './styles.scoped.css';

const ReactGridLayout = WidthProvider(RGL);

export interface DashboardGridProp {
  items: WidgetGridItem[];
  editMode?: boolean;
  onLayoutChange?: (layout: RGL.Layout) => void;
  onItemsChange: (widgets: any[]) => void;
  hideYAxisTicks?: boolean;
  apiParams?: any;
}

function DashboardGrid({
  items = [],
  editMode = false,
  onItemsChange,
  hideYAxisTicks = true,
  apiParams = {},
}: DashboardGridProp) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const mobileItemHeight = 11;

  const layout = items.map((item, index) => {
    if (isMobile) {
      return {
        ...item.layout,
        i: item.id,
        x: 0,
        w: 1,
        h: mobileItemHeight,
        y: index * mobileItemHeight,
      };
    }
    return { ...item.layout, i: item.id };
  });

  const handleLayoutChange = useCallback(
    (newLayout: any) => {
      const layoutMap: any = {};
      newLayout.forEach((layoutItem: any) => {
        layoutMap[layoutItem.i] = pick(layoutItem, ['w', 'h', 'x', 'y']);
      });
      onItemsChange(
        items.map((widget) => ({
          ...widget,
          layout: layoutMap[widget.id],
        })),
      );
    },
    [items, onItemsChange],
  );

  const handleRemove = useCallback(
    (id: any) => () => {
      onItemsChange(items.filter((item) => item.id !== id));
    },
    [items, onItemsChange],
  );

  const handleTitleChange = useCallback(
    (id: any, newTitle: string) =>
      onItemsChange(items.map((item) => (item.id === id ? { ...item, title: newTitle } : item))),
    [items, onItemsChange],
  );

  return (
    <ReactGridLayout
      key={isMobile ? 'dashboard-grid-mobile' : 'dashboard-grid-desktop'}
      containerPadding={[0, 0]}
      margin={isMobile ? [0, 12] : [10, 10]}
      cols={isMobile ? 1 : 24}
      rowHeight={isMobile ? 24 : 30}
      compactType="vertical"
      preventCollision={false}
      layout={layout}
      draggableHandle=".react-grid-item > .ant-card > .ant-card-head > .ant-card-head-wrapper > .ant-card-extra > .ant-space > .ant-space-item > .ant-btn-default"
      isDraggable={editMode && !isMobile}
      isResizable={editMode && !isMobile}
      onLayoutChange={editMode && !isMobile ? handleLayoutChange : undefined}
    >
      {items.map((widget) => (
        <div key={widget.id}>
          <Widget
            type={widget.type}
            title={widget.title}
            sums={widget.sums}
            groups={widget.groups}
            apiParams={apiParams}
            config={widget.config}
            mode={editMode ? 'edit' : 'view'}
            onRemove={editMode ? handleRemove(widget.id) : undefined}
            onTitleChange={
              editMode ? (newTitle: string) => handleTitleChange(widget.id, newTitle) : undefined
            }
            hideYAxisTicks={hideYAxisTicks}
          />
        </div>
      ))}
    </ReactGridLayout>
  );
}

export default DashboardGrid;
